import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  LinearProgress,
  Typography,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  PageHeader,
  LoadingState,
  StatusChip,
  DetailCard,
  ConfirmDialog,
  BarcodeScannerModal,
} from '../components';
import {
  useCount,
  useCountItems,
  useStartCount,
  useUpdateCountItem,
  useCompleteCount,
  useCancelCount,
  useDeleteCount,
  useLookupCountItem,
} from '../hooks/useInventoryCounts';
import { colors } from '../constants';

const ITEMS_PER_PAGE = 50;

const FILTER_TABS = [
  { label: 'All', value: undefined },
  { label: 'Uncounted', value: 'uncounted' },
  { label: 'Counted', value: 'counted' },
  { label: 'Variance', value: 'variance' },
];

export default function InventoryCountDetail() {
  const { uuid } = useParams();
  const history = useHistory();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [itemFilter, setItemFilter] = useState(undefined);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [countInput, setCountInput] = useState('');
  const [reasonInput, setReasonInput] = useState('');
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const debounceRef = useRef(null);

  const { data: count, isLoading, isError, refetch } = useCount(uuid);
  const { data: itemsData, isLoading: itemsLoading, isFetching: itemsFetching } = useCountItems(
    uuid,
    page,
    ITEMS_PER_PAGE,
    debouncedSearch || undefined,
    itemFilter
  );

  const startCount = useStartCount();
  const updateItem = useUpdateCountItem();
  const completeCount = useCompleteCount();
  const cancelCount = useCancelCount();
  const deleteCount = useDeleteCount();
  const lookupItem = useLookupCountItem();

  const showToast = (severity, message) => setToast({ severity, message });

  const handleSearchChange = (text) => {
    setSearch(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(text);
      setPage(1);
    }, 400);
  };

  const handleFilterChange = (f) => {
    setItemFilter(f);
    setPage(1);
  };

  const handleBarcodeScan = useCallback(
    async (item) => {
      try {
        const found = await lookupItem.mutateAsync({
          countUuid: uuid,
          query: item.sku || item.barcode,
        });
        setActiveItem(found);
        setCountInput('');
        setReasonInput('');
      } catch {
        showToast('error', 'Item not found in this count');
      }
    },
    [uuid, lookupItem]
  );

  const handleItemTap = (item) => {
    if (count?.status !== 'in_progress') return;
    setActiveItem(item);
    setCountInput(item.countedQuantity != null ? String(item.countedQuantity) : '');
    setReasonInput(item.varianceReason || '');
  };

  const handleSaveCount = async () => {
    if (!activeItem) return;
    const qty = parseFloat(countInput);
    if (Number.isNaN(qty) || qty < 0) {
      showToast('error', 'Enter a valid quantity');
      return;
    }
    try {
      await updateItem.mutateAsync({
        countUuid: uuid,
        productUuid: activeItem.productUuid,
        countedQuantity: qty,
        varianceReason: reasonInput || undefined,
        countedBy: 'admin',
      });
      showToast('success', `${activeItem.sku} counted: ${qty}`);
      setActiveItem(null);
      setCountInput('');
      setReasonInput('');
    } catch (e) {
      showToast('error', e?.response?.data?.message || 'Failed to save');
    }
  };

  const handleStart = async () => {
    try {
      await startCount.mutateAsync(uuid);
      showToast('success', 'Count started');
    } catch (e) {
      showToast('error', e?.response?.data?.message || 'Failed to start');
    }
  };

  const handleConfirmAction = async () => {
    const action = confirm;
    setConfirm(null);
    if (!action) return;
    try {
      if (action === 'complete') {
        await completeCount.mutateAsync({ uuid, data: { approvedBy: 'admin', createAdjustments: true } });
        showToast('success', 'Count completed — stock adjustments applied');
      } else if (action === 'cancel') {
        await cancelCount.mutateAsync(uuid);
        history.replace('/inventory-management/inventory-counts');
      } else if (action === 'delete') {
        await deleteCount.mutateAsync(uuid);
        history.replace('/inventory-management/inventory-counts');
      }
    } catch (e) {
      showToast('error', e?.response?.data?.message || 'Action failed');
    }
  };

  const progressPct = useMemo(() => {
    const total = count?.totalItems ?? count?.itemCount ?? 0;
    const counted = count?.countedItems ?? count?.countedItemCount ?? 0;
    if (total === 0) return 0;
    return Math.round((counted / total) * 100);
  }, [count]);

  if (isLoading) return <LoadingState />;

  if (isError || !count) {
    return (
      <>
        <PageHeader title="Error" backTo="/inventory-management/inventory-counts" />
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography sx={{ mb: 2, color: colors.gray500 }}>Failed to load count</Typography>
          <Button variant="contained" onClick={() => refetch()} sx={{ backgroundColor: colors.primary }}>
            Retry
          </Button>
        </Box>
      </>
    );
  }

  const totalItems = count.totalItems ?? count.itemCount ?? 0;
  const countedItems = count.countedItems ?? count.countedItemCount ?? 0;
  const totalPages = itemsData?.totalPages ?? 1;
  const isInProgress = count.status === 'in_progress';
  const isPlanned = count.status === 'planned';
  const isCompleted = count.status === 'completed';
  const isCancelled = count.status === 'cancelled';
  const rows = itemsData?.data ?? [];

  const confirmMeta = {
    complete: {
      title: 'Complete Count?',
      message:
        'This will finalize the count and apply stock adjustments for all variances. This cannot be undone.',
      confirmLabel: 'Complete',
    },
    cancel: {
      title: 'Cancel Count?',
      message: 'This will cancel the count. No stock changes will be applied.',
      confirmLabel: 'Cancel Count',
    },
    delete: {
      title: 'Delete Count?',
      message: 'This will permanently delete the count and all its items.',
      confirmLabel: 'Delete',
    },
  };

  return (
    <>
      <PageHeader title="Count Details" backTo="/inventory-management/inventory-counts" />

      <Grid container spacing={2.5}>
        <Grid item xs={12}>
          <DetailCard>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.navy }}>
                  {count.uuid.slice(0, 8)}
                </Typography>
                <Typography variant="body2" sx={{ color: colors.gray500 }}>
                  {count.type?.toUpperCase()} count
                </Typography>
              </Box>
              <StatusChip status={count.status} />
            </Stack>
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={progressPct} sx={{ height: 8, borderRadius: 4 }} />
              <Typography variant="caption" sx={{ color: colors.gray600, mt: 0.5, display: 'block' }}>
                {countedItems} / {totalItems} counted ({progressPct}%)
              </Typography>
            </Box>
          </DetailCard>
        </Grid>

        <Grid item xs={12}>
          <DetailCard>
            {(isInProgress || isCompleted) && (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <TextField
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    placeholder="Search by SKU, name, or barcode..."
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                  {isInProgress && (
                    <Button
                      variant="contained"
                      onClick={() => setScannerOpen(true)}
                      sx={{ backgroundColor: colors.primary, minWidth: 48 }}
                    >
                      <QrCodeScannerIcon />
                    </Button>
                  )}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  {FILTER_TABS.map((f) => (
                    <Button
                      key={f.label}
                      size="small"
                      variant={itemFilter === f.value ? 'contained' : 'outlined'}
                      onClick={() => handleFilterChange(f.value)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 5,
                        ...(itemFilter === f.value ? { backgroundColor: colors.primary } : {}),
                      }}
                    >
                      {f.label}
                    </Button>
                  ))}
                </Stack>
              </>
            )}

            {itemsFetching && !itemsLoading && <LinearProgress sx={{ mb: 1 }} />}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>SKU / Product</TableCell>
                    <TableCell align="right">System</TableCell>
                    <TableCell align="right">Counted</TableCell>
                    <TableCell align="right">Variance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemsLoading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4, color: colors.gray500 }}>
                        No items found
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((item, index) => {
                      const isCounted = item.countedQuantity != null;
                      const hasVariance = item.variance != null && item.variance !== 0;
                      const isActive = activeItem?.productUuid === item.productUuid;
                      return (
                        <TableRow
                          key={item.uuid || `item-${index}`}
                          hover
                          onClick={() => handleItemTap(item)}
                          sx={{
                            cursor: isInProgress ? 'pointer' : 'default',
                            backgroundColor: isActive ? colors.primaryTint : 'inherit',
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.sku || item.productUuid?.slice(0, 8) || '—'}
                              </Typography>
                              {isCounted && <CheckCircleIcon sx={{ fontSize: 15, color: colors.success }} />}
                            </Stack>
                            {item.productName && (
                              <Typography variant="caption" sx={{ color: colors.gray600 }}>
                                {item.productName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">{item.systemQuantity ?? 0}</TableCell>
                          <TableCell align="right">
                            {isCounted ? (
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {item.countedQuantity}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: colors.gray300 }}>—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {isCounted ? (
                              hasVariance ? (
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, color: item.variance > 0 ? colors.info : colors.error }}
                                >
                                  {item.variance > 0 ? '+' : ''}
                                  {item.variance}
                                </Typography>
                              ) : (
                                <Typography variant="caption" sx={{ color: colors.success }}>Match</Typography>
                              )
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} sx={{ mt: 2 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <Typography variant="body2" sx={{ color: colors.gray600 }}>
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </Button>
              </Stack>
            )}
          </DetailCard>
        </Grid>

        {activeItem && isInProgress && (
          <Grid item xs={12}>
            <DetailCard>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{activeItem.sku}</Typography>
                  {activeItem.productName && (
                    <Typography variant="body2" sx={{ color: colors.gray600 }}>{activeItem.productName}</Typography>
                  )}
                  <Typography variant="caption" sx={{ color: colors.gray500 }}>
                    System qty: {activeItem.systemQuantity}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => setActiveItem(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
                <TextField
                  label="Counted qty"
                  type="number"
                  value={countInput}
                  onChange={(e) => setCountInput(e.target.value)}
                  size="small"
                  autoFocus
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="Variance reason (optional)"
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  size="small"
                  sx={{ flex: 2 }}
                />
              </Stack>
              <Button
                variant="contained"
                onClick={handleSaveCount}
                disabled={updateItem.isPending}
                sx={{ backgroundColor: colors.accent, textTransform: 'none' }}
              >
                {updateItem.isPending ? 'Saving...' : 'Save Count'}
              </Button>
            </DetailCard>
          </Grid>
        )}

        <Grid item xs={12}>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            {isPlanned && (
              <>
                <Button
                  variant="contained"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStart}
                  disabled={startCount.isPending}
                  sx={{ backgroundColor: colors.primary, textTransform: 'none' }}
                >
                  {startCount.isPending ? 'Starting...' : 'Start Count'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setConfirm('delete')}
                  sx={{ textTransform: 'none' }}
                >
                  Delete
                </Button>
              </>
            )}
            {isInProgress && (
              <>
                <Button
                  variant="contained"
                  startIcon={<DoneAllIcon />}
                  onClick={() => setConfirm('complete')}
                  disabled={completeCount.isPending}
                  sx={{ backgroundColor: colors.accent, textTransform: 'none' }}
                >
                  Complete Count
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => setConfirm('cancel')}
                  sx={{ textTransform: 'none' }}
                >
                  Cancel Count
                </Button>
              </>
            )}
            {isCancelled && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setConfirm('delete')}
                sx={{ textTransform: 'none' }}
              >
                Delete Count
              </Button>
            )}
          </Stack>
        </Grid>
      </Grid>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeScan}
        onBarcodeNotFound={(barcode) => showToast('error', `Barcode "${barcode}" not found in this count`)}
      />

      <ConfirmDialog
        open={!!confirm}
        title={confirm ? confirmMeta[confirm].title : ''}
        message={confirm ? confirmMeta[confirm].message : ''}
        confirmLabel={confirm ? confirmMeta[confirm].confirmLabel : 'Confirm'}
        destructive={confirm === 'delete'}
        onConfirm={handleConfirmAction}
        onClose={() => setConfirm(null)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
}
